import { useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { Link, useLocation, useParams, useSearch } from 'wouter'
import { Spinner } from '../components/ui'
import { api } from '../lib/api'
import { keys, useMe } from '../lib/hooks'

const LANDING_URL = (import.meta.env.VITE_LANDING_URL as string | undefined)?.replace(/\/$/, '')

/** The left half of every sign-in screen: the same masthead and voice as the landing page. */
function Art({ invite }: { invite?: boolean }) {
  return (
    <div className="auth-art">
      <div className="auth-brand">
        {LANDING_URL ? (
          <a href={LANDING_URL} aria-label="Wombat home">
            <b>Wombat</b> <span>by Summation</span>
          </a>
        ) : (
          <div>
            <b>Wombat</b> <span>by Summation</span>
          </div>
        )}
        <span className="auth-tag">Open books. Open source.</span>
      </div>
      <div>
        {invite ? (
          <>
            <h1>Your team’s plan, in one place.</h1>
            <p>
              Change your headcount and spend by quarter, here or in a spreadsheet, see how it adds up against your target as you go, and
              submit it to FP&amp;A when you’re done.
            </p>
          </>
        ) : (
          <>
            <h1>Stop renting your operating model.</h1>
            <p>
              Your plan’s logic lives in your git repository and your data in your lake. Every change, from a person, a spreadsheet or an
              agent, arrives with its impact and tests attached.
            </p>
          </>
        )}
      </div>
      <div className="auth-foot">
        {invite ? 'PLAN · CHECK THE TARGET · SUBMIT' : 'MODEL AS CODE · CHANGES · AGENTS · EXCEL ROUND-TRIP'}
        {LANDING_URL && <a href={LANDING_URL}>← Back to wombat</a>}
      </div>
    </div>
  )
}

/** Cross-fade into the app after signing in, where the browser supports view transitions. */
function fade(change: () => void) {
  const start = document.startViewTransition?.bind(document)
  if (!start || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return change()
  start(() => flushSync(change))
}

function safeNext(raw: string | null): string {
  return raw?.startsWith('/') && !raw.startsWith('//') ? raw : '/'
}

function AuthForm({ mode }: { mode: 'login' | 'signup' }) {
  const [, navigate] = useLocation()
  const search = new URLSearchParams(useSearch())
  const next = safeNext(search.get('next'))
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (mode === 'signup') await api.post('/auth/signup', { name, email, password })
      else await api.post('/auth/login', { email, password })
      await qc.invalidateQueries({ queryKey: keys.me })
      fade(() => navigate(next))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const other = mode === 'login' ? '/signup' : '/login'
  const q = search.get('next') ? `?next=${encodeURIComponent(next)}` : ''

  return (
    <div className="auth">
      <Art />
      <div className="auth-form">
        <form className="auth-card" onSubmit={submit}>
          <div>
            <h2 className="auth-title">{mode === 'login' ? 'Sign in' : 'Create your account'}</h2>
            <p className="muted small" style={{ marginTop: 6 }}>
              {mode === 'login'
                ? 'Welcome back.'
                : 'You’ll start with a sample workspace you can explore, and you can import your own workbook any time.'}
            </p>
          </div>
          {mode === 'signup' && (
            <div className="field">
              <label htmlFor="name">Name</label>
              <input id="name" type="text" autoComplete="name" required value={name} onChange={e => setName(e.target.value)} />
            </div>
          )}
          <div className="field">
            <label htmlFor="email">Work email</label>
            <input id="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={8}
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            {mode === 'signup' && <span className="hint">At least 8 characters.</span>}
          </div>
          {error && <div className="issue">{error}</div>}
          <button className="btn primary lg" type="submit" disabled={busy} style={{ justifyContent: 'center' }}>
            {busy && <Spinner />} {mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
          <div className="small muted" style={{ textAlign: 'center' }}>
            {mode === 'login' ? 'New here? ' : 'Already have an account? '}
            <Link href={`${other}${q}`}>{mode === 'login' ? 'Create an account' : 'Sign in'}</Link>
          </div>
        </form>
      </div>
    </div>
  )
}

export const Login = () => <AuthForm mode="login" />
export const Signup = () => <AuthForm mode="signup" />

type InvitePreview = {
  workspace: string
  email: string
  role: string
  owns: { id: string; label: string }[]
  ownerLevel: string | null
  inviter: string | null
  accepted: boolean
  expired: boolean
}

const ROLE_WORDS: Record<string, string> = {
  owner: 'an owner',
  admin: 'an admin',
  planner: 'a planner',
  contributor: 'a contributor',
  viewer: 'a viewer',
}

/**
 * An invite link, for someone who may never have heard of this app: who invited them, to what,
 * and what they'll be planning, then one step to join (create an account, sign in, or accept).
 * Joining from an invite goes straight to the plan they own, with no sample workspace.
 */
export function InviteAccept() {
  const { token } = useParams<{ token: string }>()
  const [, navigate] = useLocation()
  const qc = useQueryClient()
  const me = useMe()
  const [preview, setPreview] = useState<InvitePreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    api
      .get<InvitePreview>(`/auth/invites/${encodeURIComponent(token)}`)
      .then(p => {
        setPreview(p)
        setEmail(p.email)
      })
      .catch(e => setError((e as Error).message))
  }, [token])

  const go = async (wid: string) => {
    await qc.invalidateQueries({ queryKey: keys.me })
    navigate(preview?.owns.length ? `/w/${wid}/plan` : `/w/${wid}`, { replace: true })
  }
  const run = async (f: () => Promise<string>) => {
    setBusy(true)
    setError(null)
    try {
      await go(await f())
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const accept = () =>
    run(async () => (await api.post<{ workspaceId: string }>(`/auth/invites/${encodeURIComponent(token)}/accept`)).workspaceId)
  const signup = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => (await api.post<{ workspaceId: string }>('/auth/signup', { name, email, password, invite: token })).workspaceId)
  }

  const signedIn = !!me.data
  const dead = preview && (preview.accepted || preview.expired)
  const owns = preview?.owns.map(o => o.label).join(', ')
  return (
    <div className="auth">
      <Art invite />
      <div className="auth-form">
        <form className="auth-card" onSubmit={signup}>
          {!preview && !error && (
            <div className="row muted">
              <Spinner /> Opening your invite…
            </div>
          )}
          {preview && (
            <div>
              <div className="eyebrow">{preview.inviter ? `${preview.inviter} invited you` : 'You’re invited'}</div>
              <h2 className="auth-title" style={{ marginTop: 6 }}>
                Join {preview.workspace}
              </h2>
              <p className="muted small" style={{ marginTop: 8, lineHeight: 1.55 }}>
                {preview.owns.length
                  ? `You’ll plan ${owns}: enter your ${preview.owns.length > 1 ? 'numbers' : 'team’s numbers'} for the year, see how they add up against the target, and submit them for review.`
                  : `You’ll join as ${ROLE_WORDS[preview.role] ?? preview.role}.`}
              </p>
            </div>
          )}
          {dead && (
            <div className="issue">
              {preview?.accepted ? 'This invite has already been used.' : 'This invite has expired.'} Ask{' '}
              {preview?.inviter ?? 'the person who sent it'} for a new link, or <Link href="/login">sign in</Link> if you’ve already joined.
            </div>
          )}
          {preview && !dead && signedIn && (
            <>
              <div className="small muted">
                Signed in as <b>{me.data?.user.email}</b>
                {me.data?.user.email.toLowerCase() !== preview.email.toLowerCase() && (
                  <span className="x-bad"> (the invite was sent to {preview.email})</span>
                )}
              </div>
              <button className="btn primary lg" type="button" onClick={accept} disabled={busy} style={{ justifyContent: 'center' }}>
                {busy && <Spinner />} Join {preview.workspace}
              </button>
            </>
          )}
          {preview && !dead && !signedIn && (
            <>
              <div className="field">
                <label htmlFor="name">Your name</label>
                <input id="name" type="text" autoComplete="name" required value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="email">Work email</label>
                <input id="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="password">Choose a password</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <span className="hint">At least 8 characters.</span>
              </div>
              <button className="btn primary lg" type="submit" disabled={busy} style={{ justifyContent: 'center' }}>
                {busy && <Spinner />} Create account and join
              </button>
              <div className="small muted" style={{ textAlign: 'center' }}>
                Already have an account? <Link href={`/login?next=${encodeURIComponent(`/invite/${token}`)}`}>Sign in to join</Link>
              </div>
            </>
          )}
          {error && <div className="issue">{error}</div>}
          {error && !preview && <Link href="/">Go to your workspaces</Link>}
        </form>
      </div>
    </div>
  )
}
